import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './DashboardBoard.css'

const MESH = 16
const STORAGE_PREFIX = 'wfmed-dashboard-layout'

function snap(value) {
  return Math.max(0, Math.round(value / MESH) * MESH)
}

function intersects(left, right) {
  const pad = 2
  return left.x + pad < right.x + right.w
    && left.x + left.w > right.x + pad
    && left.y + pad < right.y + right.h
    && left.y + left.h > right.y + pad
}

function sameIds(left, right) {
  if (left.size !== right.size) return false
  for (const id of left) {
    if (!right.has(id)) return false
  }
  return true
}

function readLayout(userId) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}:${userId || 'local'}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function sizesFor(width) {
  const gauge = 11 * 16
  const week = Math.max(168, Math.min(22 * 16, width / 2 - gauge - 24))
  const side = Math.max(220, width / 2 - 16)
  return { gauge, week, feed: side, heart: side, fever: side, oxygen: side, pressure: side, glucose: side }
}

function defaultPositions(width) {
  const gauge = 11 * 16
  const right = snap(width / 2 + 8)
  const underWater = snap(13 * 16)
  const underFeed = snap(31 * 16)
  const step = snap(22 * 16)
  return {
    gauge: { x: 0, y: 0 },
    week: { x: snap(gauge + 16), y: 0 },
    feed: { x: right, y: 0 },
    heart: { x: 0, y: underWater },
    oxygen: { x: 0, y: underWater + step },
    glucose: { x: 0, y: underWater + step * 2 },
    fever: { x: right, y: underFeed },
    pressure: { x: right, y: underFeed + step }
  }
}

function DashboardBoard({ userId, items = [] }) {
  const boardRef = useRef(null)
  const nodeRefs = useRef({})
  const editingRef = useRef(false)
  const snapshotRef = useRef(null)
  const [editing, setEditing] = useState(false)
  const [positions, setPositions] = useState(null)
  const [widths, setWidths] = useState({})
  const [overlaps, setOverlaps] = useState(() => new Set())
  const [boardHeight, setBoardHeight] = useState(0)
  const [notice, setNotice] = useState('')
  const [toolsHost, setToolsHost] = useState(null)
  editingRef.current = editing
  const visible = items.filter((item) => item.content)
  const visibleRef = useRef(visible)
  visibleRef.current = visible

  useLayoutEffect(() => {
    const node = boardRef.current
    if (!node) return undefined
    const apply = () => {
      const width = node.clientWidth
      if (!width) return
      if (window.matchMedia('(max-width: 1023px)').matches && editingRef.current) setEditing(false)
      setWidths(sizesFor(width))
      setPositions((current) => {
        if (editingRef.current && current) return current
        const saved = readLayout(userId)
        const next = saved?.items
          ? Object.fromEntries(Object.entries(saved.items).map(([id, point]) => [
            id,
            { x: snap(point.x * (width / (saved.width || width))), y: snap(point.y) }
          ]))
          : defaultPositions(width)
        if (current && JSON.stringify(current) === JSON.stringify(next)) return current
        return next
      })
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(node)
    return () => observer.disconnect()
  }, [userId])

  const visibleKey = visible.map((item) => item.id).join('|')

  useLayoutEffect(() => {
    setToolsHost(document.querySelector('[data-dashboard-layout-tools]'))
  }, [])

  useLayoutEffect(() => {
    if (!positions) return undefined
    const board = boardRef.current
    if (!board) return undefined
    const measure = () => {
      const origin = board.getBoundingClientRect()
      const boxes = visibleRef.current.flatMap((item) => {
        const node = nodeRefs.current[item.id]
        if (!node || !positions[item.id]) return []
        const rect = node.getBoundingClientRect()
        return [{
          id: item.id,
          x: rect.left - origin.left,
          y: rect.top - origin.top,
          w: rect.width,
          h: rect.height
        }]
      })
      const hits = new Set()
      boxes.forEach((left, index) => {
        boxes.slice(index + 1).forEach((right) => {
          if (intersects(left, right)) {
            hits.add(left.id)
            hits.add(right.id)
          }
        })
      })
      setOverlaps((current) => (sameIds(current, hits) ? current : hits))
      const bottom = boxes.reduce((max, box) => Math.max(max, box.y + box.h), 0) + 12
      setBoardHeight((current) => (Math.abs(current - bottom) < 1 ? current : bottom))
    }
    measure()
    const observer = new ResizeObserver(measure)
    visibleRef.current.forEach((item) => {
      const node = nodeRefs.current[item.id]
      if (node) observer.observe(node)
    })
    return () => observer.disconnect()
  }, [positions, widths, visibleKey, editing])

  const startDrag = (id, event) => {
    if (!editing) return
    event.preventDefault()
    event.stopPropagation()
    const origin = positions?.[id]
    if (!origin) return
    const startX = event.clientX
    const startY = event.clientY
    const boardWidth = boardRef.current?.clientWidth || 0
    const widgetWidth = widths[id] || 0
    const move = (ev) => {
      const x = Math.min(snap(origin.x + ev.clientX - startX), Math.max(0, boardWidth - widgetWidth))
      const y = snap(origin.y + ev.clientY - startY)
      setPositions((current) => ({ ...current, [id]: { x, y } }))
      setNotice('')
    }
    const stop = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
  }

  const beginEdit = () => {
    snapshotRef.current = positions
    setNotice('')
    setEditing(true)
  }

  const cancelEdit = () => {
    setPositions(snapshotRef.current)
    setNotice('')
    setEditing(false)
  }

  const saveEdit = () => {
    if (overlaps.size) {
      setNotice('Separe os cartões que estão uns sobre os outros para salvar.')
      return
    }
    const width = boardRef.current?.clientWidth || 0
    const itemsToSave = {}
    visible.forEach((item) => {
      if (positions?.[item.id]) itemsToSave[item.id] = positions[item.id]
    })
    localStorage.setItem(`${STORAGE_PREFIX}:${userId || 'local'}`, JSON.stringify({
      width,
      items: itemsToSave
    }))
    setNotice('')
    setEditing(false)
  }

  const tools = (
      <div className="dashboard-board-tools">
        {editing ? (
          <>
            <button type="button" className="patients-btn patients-btn--primary patients-btn--compact" onClick={saveEdit} disabled={overlaps.size > 0}>
              Salvar posições
            </button>
            <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={cancelEdit}>
              Cancelar
            </button>
            <p className={`dashboard-board-note${overlaps.size ? ' is-blocked' : ''}`}>
              {overlaps.size
                ? 'Há cartões sobrepostos. Na edição isso pode acontecer; para salvar, cada um precisa do seu espaço.'
                : 'Arraste pelo botão Mover. A malha só orienta o lugar.'}
            </p>
          </>
        ) : (
          <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={beginEdit}>
            Editar organização
          </button>
        )}
        {notice && <p className="dashboard-board-note is-blocked">{notice}</p>}
      </div>
  )

  return (
    <div className="dashboard-board-wrap">
      {toolsHost ? createPortal(tools, toolsHost) : tools}
      <div
        ref={boardRef}
        className={`dashboard-board${editing ? ' is-editing' : ''}`}
        style={{ height: positions ? boardHeight : undefined }}
      >
        {visible.map((item) => {
          const point = positions?.[item.id] || { x: 0, y: 0 }
          return (
            <div
              key={item.id}
              ref={(node) => { nodeRefs.current[item.id] = node }}
              className={`dashboard-widget is-${item.id}${overlaps.has(item.id) ? ' is-overlap' : ''}`}
              style={{ left: point.x, top: point.y, width: widths[item.id] }}
              data-onboarding={item.anchor || undefined}
            >
              {editing && (
                <button type="button" className="dashboard-widget-handle" onPointerDown={(event) => startDrag(item.id, event)}>
                  Mover
                </button>
              )}
              {item.content}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DashboardBoard
